import React from "react";
import {
  arrayOf,
  bool,
  func,
  node,
  number,
  oneOfType,
  string,
} from "prop-types";
import { twMerge } from "tailwind-merge";

import TestDrivePointLogo from "./TestDrivePointLogo";
import Icon from "@components/shared/Icons/Icon";
import RequestButton from "@components/shared/RequestButton";
import Typography from "@components/shared/Typography";
import { hyphenate } from "@components/shared/utils";

import { useTranslation } from "@lib/i18n";
import { testDriveType } from "@types";

const FilterResultItemWrapper = ({
  name,
  testDriveType,
  children,
  distanceInMeters,
  isActive,
  onPick,
  onPickSuccess,
  storeType,
}) => {
  const { t } = useTranslation();

  const convertToKilometers = (distanceInMeters, precision = 2) =>
    (distanceInMeters / 1000).toFixed(precision);

  return (
    <div
      className={twMerge(
        "rounded-md px-2 py-4 grid grid-template-mapSelectionItem gap-2 transition duration-300 border",
        isActive
          ? "bg-white border-greyLight"
          : "border-greyLight/50 hover:bg-white/90"
      )}
    >
      <div className="grid-area-icon flex items-start justify-center">
        <Icon
          className={twMerge(
            "w-6 h-auto",
            isActive ? "text-primaryOrange" : "text-primaryDark"
          )}
          icon={
            <TestDrivePointLogo
              testDriveType={testDriveType}
              storeType={storeType}
            />
          }
        />
      </div>

      <div className="grid-area-name">
        <Typography
          className={twMerge(
            "font-semibold",
            isActive ? "text-primaryOrange" : "text-primaryDark"
          )}
          data-id={`point-name-${hyphenate(name, /\.|\s/g)}`}
        >
          {name}
        </Typography>
      </div>
      <div className="grid-area-address">
        <Typography className="text-sm">{children}</Typography>
      </div>
      {!!distanceInMeters && (
        <div className="grid-area-distance flex items-center min-[32rem]:justify-end min-[32rem]:items-start">
          <Typography
            className="text-sm whitespace-nowrap"
            data-id={`delivery-point-distance-${distanceInMeters}`}
          >
            {convertToKilometers(distanceInMeters)}{" "}
            {t("general.kilometerSymbol")}
          </Typography>
        </div>
      )}

      <div className="grid-area-button">
        <div className="flex justify-end items-end size-full">
          <RequestButton
            className="text-xs"
            data-id="filtered-result-item-select-button"
            onRequestSuccess={onPickSuccess}
            triggerRequestCallback={onPick}
            size="small"
            variant={isActive ? "primary" : "secondary"}
          >
            {isActive ? t("testDrive.selected") : t("testDrive.select")}
          </RequestButton>
        </div>
      </div>
    </div>
  );
};

FilterResultItemWrapper.propTypes = {
  testDriveType: testDriveType,
  name: string.isRequired,
  children: oneOfType([node, arrayOf(node)]),
  distanceInMeters: number,
  isActive: bool,
  onPick: func.isRequired,
  onPickSuccess: func,
  storeType: string,
};

export default FilterResultItemWrapper;
